import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { AlertTriangle, Radio, ShieldAlert, Activity, XCircle, MapPin, Clock, Search, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { useStore } from '../data/store';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { EmptyState } from '../components/EmptyState';
import { ExportDropdown } from '../components/ExportDropdown';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Filter } from 'lucide-react';
export default function Alerts() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const alertsList = useStore(s => s.alerts);
  const filteredAlerts = alertsList.filter(alert => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || alert.desc.toLowerCase().includes(search) || alert.id.toLowerCase().includes(search);
    const matchesLevel = !filterValues.level || filterValues.level === 'all' || alert.type === filterValues.level;
    const matchesSource = !filterValues.source || filterValues.source === 'all' || alert.source === filterValues.source;
    // Simple location match
    const matchesLocation = !filterValues.location || filterValues.location === 'all' || alert.location.includes(filterValues.location);
    return matchesSearch && matchesLevel && matchesSource && matchesLocation;
  });
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari ID, deskripsi...'
  }, {
    key: 'date',
    label: 'Tanggal',
    type: 'datePreset'
  }, {
    key: 'level',
    label: 'Level Alert',
    type: 'urgencyBadge',
    options: [{
      label: 'Kritis',
      value: 'KRITIS'
    }, {
      label: 'Waspada',
      value: 'WASPADA'
    }, {
      label: 'Info',
      value: 'INFO'
    }]
  }, {
    key: 'source',
    label: 'Sumber',
    type: 'select',
    options: [{
      label: 'SIGINT',
      value: 'SIGINT'
    }, {
      label: 'HUMINT',
      value: 'HUMINT'
    }, {
      label: 'OSINT',
      value: 'OSINT'
    }, {
      label: 'CYBER',
      value: 'CYBER'
    }]
  }, {
    key: 'location',
    label: 'Wilayah',
    type: 'select',
    options: [{
      label: 'Nasional',
      value: 'NASIONAL'
    }, {
      label: 'Papua',
      value: 'PAPUA'
    }, {
      label: 'DKI Jakarta',
      value: 'DKI JAKARTA'
    }, {
      label: 'Jawa Barat',
      value: 'JAWA BARAT'
    }]
  }];
  const exportColumns = [{
    key: 'id',
    label: 'ID'
  }, {
    key: 'type',
    label: 'Tipe'
  }, {
    key: 'desc',
    label: 'Deskripsi'
  }, {
    key: 'source',
    label: 'Sumber'
  }, {
    key: 'location',
    label: 'Lokasi'
  }, {
    key: 'time',
    label: 'Waktu'
  }];
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-687">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-688">
        <div data-id="element-689">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-690">
            <AlertTriangle className="w-6 h-6 text-destructive text-glow-destructive animate-pulse" data-id="element-691" />
            DENS CAKRA EARLY WARNING CENTER
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-692">
            National Alert &amp; Incident Monitoring System
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-693">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-694">
            <Filter className="w-4 h-4 mr-2" data-id="element-695" />
            Filter
          </Button>
          <div className="glass-panel px-3 py-1.5 rounded flex items-center gap-2 border-destructive/30" data-id="element-696">
            <span className="relative flex h-2 w-2" data-id="element-697">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" data-id="element-698"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" data-id="element-699"></span>
            </span>
            <span className="text-xs font-mono text-destructive tracking-widest text-glow-destructive" data-id="element-700">
              DEFCON 3
            </span>
          </div>
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredAlerts.length} exportData={filteredAlerts} exportColumns={exportColumns} exportFilename="Peringatan_Dini_DENS_CAKRA" data-id="element-701" />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-702">
        <div className={`space-y-4 transition-all duration-300 lg:col-span-2`} data-id="element-703">
          {filteredAlerts.length === 0 ? <EmptyState icon={CheckCircle2} title="TIDAK ADA PERINGATAN AKTIF" description="Sistem tidak mendeteksi ancaman atau peringatan yang sesuai dengan kriteria. Sektor saat ini terpantau aman." actionLabel="Bersihkan Filter" onAction={handleResetFilters} data-id="element-704" /> : filteredAlerts.map(alert => <Card key={alert.id} className={`glass-panel-heavy border-l-4 cursor-pointer hover:bg-white/5 transition-colors ${alert.type === 'KRITIS' ? 'border-l-destructive shadow-[0_0_15px_rgba(var(--destructive),0.1)]' : alert.type === 'WASPADA' ? 'border-l-[var(--status-standby)] shadow-[0_0_15px_rgba(var(--status-standby),0.1)]' : 'border-l-primary shadow-[0_0_15px_rgba(var(--primary),0.1)]'}`} onClick={() => navigate(`/alerts/${alert.id}`)} data-id="element-705">
                <CardContent className="p-4 flex gap-4" data-id="element-706">
                  <div className="mt-1" data-id="element-707">
                    {alert.type === 'KRITIS' ? <div className="p-2 bg-destructive/10 rounded border border-destructive/30 shadow-[inset_0_0_10px_rgba(var(--destructive),0.2)]" data-id="element-708">
                        <ShieldAlert className="w-5 h-5 text-destructive" data-id="element-709" />
                      </div> : alert.type === 'WASPADA' ? <div className="p-2 bg-[var(--status-standby)]/10 rounded border border-[var(--status-standby)]/30 shadow-[inset_0_0_10px_rgba(var(--status-standby),0.2)]" data-id="element-710">
                        <AlertTriangle className="w-5 h-5 text-[var(--status-standby)]" data-id="element-711" />
                      </div> : <div className="p-2 bg-primary/10 rounded border border-primary/30 shadow-[inset_0_0_10px_rgba(var(--primary),0.2)]" data-id="element-712">
                        <Radio className="w-5 h-5 text-primary" data-id="element-713" />
                      </div>}
                  </div>
                  <div className="flex-1" data-id="element-714">
                    <div className="flex justify-between items-start mb-1" data-id="element-715">
                      <div className="flex items-center gap-2" data-id="element-716">
                        <span className={`font-mono text-sm font-bold ${alert.type === 'KRITIS' ? 'text-destructive text-glow-destructive' : ''}`} data-id="element-717">
                          {alert.id}
                        </span>
                        <UrgencyBadge urgency={alert.type} data-id="element-718" />
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground tracking-widest" data-id="element-719">
                        {alert.time}
                      </span>
                    </div>
                    <p className="text-sm mt-2 font-mono text-foreground/90" data-id="element-720">
                      {alert.desc}
                    </p>
                    <div className="flex gap-4 mt-3 pt-3 border-t border-border/30 text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-721">
                      <span className="flex items-center gap-1" data-id="element-722">
                        <Activity className="w-3 h-3" data-id="element-723" /> Sumber:{' '}
                        <span className="text-primary" data-id="element-724">{alert.source}</span>
                      </span>
                      <span className="flex items-center gap-1" data-id="element-725">
                        <Radio className="w-3 h-3" data-id="element-726" /> Lokasi:{' '}
                        <span className="text-foreground" data-id="element-727">
                          {alert.location}
                        </span>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
        </div>

        <div className="space-y-6" data-id="element-728">
          <Card className="glass-panel-heavy border-border/50" data-id="element-729">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-730">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-731">
                <Activity className="w-4 h-4 text-primary" data-id="element-732" /> Status Sistem
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-6" data-id="element-733">
              <div data-id="element-734">
                <div className="flex justify-between text-xs mb-2" data-id="element-735">
                  <span className="font-mono text-muted-foreground tracking-widest uppercase" data-id="element-736">
                    Tingkat Ancaman
                  </span>
                  <span className="font-mono text-[var(--status-standby)] text-glow-standby tracking-widest" data-id="element-737">
                    MENINGKAT
                  </span>
                </div>
                <div className="h-2 bg-black/50 rounded-full overflow-hidden border border-border/50" data-id="element-738">
                  <div className="h-full bg-[var(--status-standby)] w-[65%] shadow-[0_0_10px_var(--status-standby)]" data-id="element-739"></div>
                </div>
              </div>
              <div data-id="element-740">
                <div className="flex justify-between text-xs mb-2" data-id="element-741">
                  <span className="font-mono text-muted-foreground tracking-widest uppercase" data-id="element-742">
                    Keamanan Jaringan
                  </span>
                  <span className="font-mono text-[var(--status-active)] text-glow-active tracking-widest" data-id="element-743">
                    AMAN
                  </span>
                </div>
                <div className="h-2 bg-black/50 rounded-full overflow-hidden border border-border/50" data-id="element-744">
                  <div className="h-full bg-[var(--status-active)] w-[95%] shadow-[0_0_10px_var(--status-active)]" data-id="element-745"></div>
                </div>
              </div>
              <div data-id="element-746">
                <div className="flex justify-between text-xs mb-2" data-id="element-747">
                  <span className="font-mono text-muted-foreground tracking-widest uppercase" data-id="element-748">
                    Relai Komunikasi
                  </span>
                  <span className="font-mono text-primary text-glow-primary tracking-widest" data-id="element-749">
                    OPTIMAL
                  </span>
                </div>
                <div className="h-2 bg-black/50 rounded-full overflow-hidden border border-border/50" data-id="element-750">
                  <div className="h-full bg-primary w-[88%] shadow-[0_0_10px_var(--primary)]" data-id="element-751"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}